import { AdminUser } from 'tools/bff/generated/bff/admin/admin_user_pb';
import { Box, Card, GDABadge, Grid, Loader, Text, tokens } from '@tocktix/pantry';
import { FunctionComponent } from 'react';
import { GdusUser } from 'tools/bff/generated/user/gdus_user_pb';

interface UserCardsProps {
  isLoading: boolean;
  results?: AdminUser[];
  onSelectUser: (gdusUserId: string, user: AdminUser) => void;
}

const getUserValue = (user: GdusUser | undefined, keys: Array<keyof GdusUser>) => {
  if (!user) {
    return '';
  }
  for (const key of keys) {
    const value = user[key];
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return '';
};

const getFullName = (user: AdminUser): string => {
  const first = user.gdusUser?.firstName ?? '';
  const last = user.gdusUser?.lastName ?? '';
  const fullName = `${first} ${last}`.trim();
  return fullName || 'Unknown User';
};

const UserCards: FunctionComponent<UserCardsProps> = ({
  isLoading,
  results = [],
  onSelectUser,
}) => {
  if (isLoading) {
    return (
      <Box padding={[300, 0]} display="flex" justifyContent="center">
        <Loader aria-label="users-loading" size="lg" />
      </Box>
    );
  }

  if (!results.length) {
    return (
      <Box padding={[300, 0]}>
        <Text variant="body" color="fgSubtle">
          No users found.
        </Text>
      </Box>
    );
  }

  return (
    <Grid gap={200}>
      {results.map((user, index) => {
        const gdusUUID = getUserValue(user.gdusUser, ['gdusUuid']);
        const resyUserId = getUserValue(user.gdusUser, ['resyUserId']);
        const tockPatronId = getUserValue(user.gdusUser, ['tockPatronId']);
        const name = getFullName(user);
        const email = getUserValue(user.gdusUser, ['email']);
        const phoneNumber = getUserValue(user.gdusUser, ['phoneNumber']);
        // TODO: replace with real isActive field once added to the AdminUser proto
        const isActive = true;

        return (
          <Grid item span={12} key={gdusUUID || `${email}-${index}`}>
            <Card
              role="button"
              tabIndex={0}
              aria-label={`Open user details for ${name || email || gdusUUID}`}
              onClick={() => gdusUUID && onSelectUser(gdusUUID, user)}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  gdusUUID && onSelectUser(gdusUUID, user);
                }
              }}
              style={{
                border: `1px solid ${tokens.color.borderDefault}`,
                cursor: gdusUUID ? 'pointer' : 'default',
              }}
            >
              <Card.Header
                border
                startElement={<GDABadge size={24} color="blueberry" />}
                title={name || 'Unknown User'}
                titleVariant="bodyLgStrong"
                endElement={
                  <Box display="flex" alignItems="center" gap={100}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: isActive
                          ? tokens.color.fgSuccessBase
                          : tokens.color.fgSubtle,
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <Text variant="bodySm" color={isActive ? 'fgSuccessBase' : 'fgSubtle'}>
                      {isActive ? 'Active' : 'Inactive'}
                    </Text>
                  </Box>
                }
              />

              <Box padding={300}>
                <Box display="flex" flexDirection="column" gap={150}>
                  <Text variant="body">
                    <strong>Email:</strong> {email || '—'}
                  </Text>
                  <Text variant="body">
                    <strong>GDUS ID:</strong> {gdusUUID || '—'}
                  </Text>
                  <Text variant="body">
                    <strong>Resy ID:</strong> {resyUserId || '—'}
                  </Text>
                  <Text variant="body">
                    <strong>Tock Patron ID:</strong> {tockPatronId || '—'}
                  </Text>
                  <Text variant="body">
                    <strong>Phone Number:</strong> {phoneNumber || '—'}
                  </Text>
                </Box>
              </Box>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default UserCards;
